import { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  BarChart3, 
  Settings, 
  Trash2, 
  Edit2, 
  Check, 
  X,
  Loader2,
  AlertCircle,
  Shield,
  Activity,
  Database,
  TrendingUp
} from 'lucide-react';
import { adminAPI, UserWithStats, SystemStats } from '@/services/admin';
import { useAuthStore } from '@/store';
import { cn } from '@/utils/helpers';
import { Sidebar } from '@/components/Sidebar';

export function Admin() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'users' | 'stats'>('users');
  
  // 用户列表状态
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 系统统计状态
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // 编辑状态
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editQuota, setEditQuota] = useState('');
  const [editUsed, setEditUsed] = useState('');
  const [saving, setSaving] = useState(false);
  
  // 删除确认
  const [deletingUser, setDeletingUser] = useState<string | null>(null);

  // 检查是否为管理员
  const isAdmin = user?.role === 'admin';

  // 加载用户列表
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminAPI.getUsers();
      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        setError(response.message || '加载失败');
      }
    } catch (err: any) {
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 加载系统统计
  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await adminAPI.getSystemStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // 初始加载
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadStats();
    }
  }, [isAdmin, loadUsers, loadStats]);

  // 开始编辑
  const startEdit = (user: UserWithStats) => {
    setEditingUser(user.id);
    setEditQuota(user.api_quota.toString());
    setEditUsed(user.api_used.toString());
  };

  // 取消编辑
  const cancelEdit = () => {
    setEditingUser(null);
    setEditQuota('');
    setEditUsed('');
  };

  // 保存编辑
  const saveEdit = async (userId: string) => {
    try {
      setSaving(true);
      const quota = parseInt(editQuota);
      const used = parseInt(editUsed);
      
      if (isNaN(quota) || quota < 0 || isNaN(used) || used < 0) {
        alert('请输入有效的非负数');
        return;
      }

      const response = await adminAPI.updateUserQuota(userId, { api_quota: quota, api_used: used });
      if (response.success) {
        await loadUsers();
        cancelEdit();
      } else {
        alert(response.message || '更新失败');
      }
    } catch (err: any) {
      alert(err.message || '更新失败');
    } finally {
      setSaving(false);
    }
  };

  // 删除用户
  const deleteUser = async (userId: string) => {
    try {
      const response = await adminAPI.deleteUser(userId);
      if (response.success) {
        await loadUsers();
        setDeletingUser(null);
      } else {
        alert(response.message || '删除失败');
      }
    } catch (err: any) {
      alert(err.message || '删除失败');
    }
  };

  // 格式化日期
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  // 格式化数字
  const formatNumber = (num: number) => {
    return num.toLocaleString('zh-CN');
  };

  if (!isAdmin) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 overflow-y-auto min-w-0 flex items-center justify-center">
          <div className="text-center">
            <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">需要管理员权限</h2>
            <p className="text-gray-500">您没有权限访问管理后台</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-y-auto min-w-0">
        <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="w-6 h-6" />
          管理后台
        </h1>
        <p className="text-gray-500 mt-1">管理系统用户配额和查看统计数据</p>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">总用户数</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '-' : formatNumber(stats?.total_users || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <Activity className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">总操作次数</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '-' : formatNumber(stats?.total_operations || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Database className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">总配额 / 已使用</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '-' : `${formatNumber(stats?.total_used || 0)} / ${formatNumber(stats?.total_quota || 0)}`}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">平均使用率</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '-' : `${stats?.avg_usage_percent || 0}%`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 标签页 */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('users')}
              className={cn(
                'py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2',
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <Users className="w-4 h-4" />
              用户管理
              <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                {users.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={cn(
                'py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2',
                activeTab === 'stats'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <BarChart3 className="w-4 h-4" />
              统计分析
            </button>
          </nav>
        </div>

        {/* 用户管理标签 */}
        {activeTab === 'users' && (
          <div className="p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">用户名</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">邮箱</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">角色</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">状态</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">配额</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">使用率</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">注册时间</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900">{u.username}</div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">{u.email}</td>
                        <td className="py-3 px-4">
                          <span className={cn(
                            'px-2 py-1 rounded-full text-xs font-medium',
                            u.role === 'admin' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-gray-100 text-gray-700'
                          )}>
                            {u.role === 'admin' ? '管理员' : '用户'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={cn(
                            'px-2 py-1 rounded-full text-xs font-medium',
                            u.status === 'active' && 'bg-green-100 text-green-700',
                            u.status === 'inactive' && 'bg-yellow-100 text-yellow-700',
                            u.status === 'banned' && 'bg-red-100 text-red-700'
                          )}>
                            {u.status === 'active' ? '正常' : u.status === 'inactive' ? '未激活' : '已禁用'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {editingUser === u.id ? (
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500 w-12">总额度:</span>
                                <input
                                  type="number"
                                  value={editQuota}
                                  onChange={(e) => setEditQuota(e.target.value)}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  min="0"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500 w-12">已使用:</span>
                                <input
                                  type="number"
                                  value={editUsed}
                                  onChange={(e) => setEditUsed(e.target.value)}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  min="0"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-600">
                              <div>{formatNumber(u.api_used)} / {formatNumber(u.api_quota)}</div>
                              <div className="text-xs text-gray-400">剩余: {formatNumber(u.remaining)}</div>
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={cn(
                                  'h-full rounded-full',
                                  u.usage_percent >= 90 ? 'bg-red-500' : 
                                  u.usage_percent >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                                )}
                                style={{ width: `${Math.min(u.usage_percent, 100)}%` }}
                              />
                            </div>
                            <span className={cn(
                              'text-xs font-medium',
                              u.usage_percent >= 90 ? 'text-red-600' : 
                              u.usage_percent >= 70 ? 'text-yellow-600' : 'text-green-600'
                            )}>
                              {u.usage_percent}%
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {formatDate(u.created_at)}
                        </td>
                        <td className="py-3 px-4">
                          {editingUser === u.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => saveEdit(u.id)}
                                disabled={saving}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                              >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={cancelEdit}
                                disabled={saving}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => startEdit(u)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="编辑配额"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeletingUser(u.id)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="删除用户"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* 统计分析标签 */}
        {activeTab === 'stats' && (
          <div className="p-6">
            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : stats ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">热门操作类型 TOP 10</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {stats.top_operations.length > 0 ? (
                      <div className="space-y-3">
                        {stats.top_operations.map((op, index) => (
                          <div key={op.operation} className="flex items-center gap-4">
                            <span className="w-6 text-center text-gray-500 font-medium">{index + 1}</span>
                            <div className="flex-1">
                              <div className="flex justify-between mb-1">
                                <span className="text-sm font-medium text-gray-700">
                                  {op.operation === 'video_info' && '获取视频信息'}
                                  {op.operation === 'hot_comments' && '获取热门评论'}
                                  {op.operation === 'search_comments' && '搜索评论'}
                                  {op.operation === 'reply_comment' && '回复评论'}
                                  {op.operation === 'batch_reply' && '批量回复'}
                                  {op.operation === 'user_info' && '获取用户信息'}
                                  {op.operation === 'video_list' && '获取视频列表'}
                                  {op.operation === 'hot_videos' && '获取热门视频'}
                                  {op.operation === 'search_videos' && '搜索视频'}
                                  {op.operation === 'sentiment_analysis' && '评论倾向分析'}
                                  {op.operation === 'parse_profile' && '解析视频和作者'}
                                  {op.operation === 'chat' && '聊天对话'}
                                  {!['video_info', 'hot_comments', 'search_comments', 'reply_comment', 'batch_reply', 'user_info', 'video_list', 'hot_videos', 'search_videos', 'sentiment_analysis', 'parse_profile', 'chat'].includes(op.operation) && op.operation}
                                </span>
                                <span className="text-sm text-gray-500">{formatNumber(op.count)} 次</span>
                              </div>
                              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full"
                                  style={{ 
                                    width: `${stats.total_operations > 0 ? (op.count / stats.total_operations) * 100 : 0}%` 
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">暂无操作记录</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">加载失败</p>
            )}
          </div>
        )}
      </div>

      {/* 删除确认对话框 */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">确认删除用户？</h3>
            <p className="text-gray-500 mb-6">
              此操作将永久删除该用户及其所有使用记录，不可恢复。
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingUser(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                取消
              </button>
              <button
                onClick={() => deleteUser(deletingUser)}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </main>
    </div>
  );
}
